import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ShoppingBag,
  ShoppingCart,
  Users,
  Radio,
  ChevronDown,
  Zap,
  Store,
  MessageSquare,
  Bot,
  Settings,
  LogOut,
  Shield,
  MoreVertical,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/hooks/useTenant';
import { usePluginsList } from '@/plugins/usePlugins';
import { PLUGIN_DEFINITIONS } from '@/plugins/registry';
import { SUPER_ADMIN_EMAILS } from '@/constants/admin';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  href: string;
  comingSoon?: boolean;
}

interface NavGroup {
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

const routePrefetchMap: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/pages/Dashboard'),
  '/vendas': () => import('@/pages/Vendas'),
  '/clientes': () => import('@/pages/Clientes'),
  '/produtos': () => import('@/pages/Produtos'),
  '/configuracoes': () => import('@/pages/Configuracoes'),
  '/lives': () => import('@/pages/LiveShop'),
  '/automacoes': () => import('@/pages/Automacoes'),
  '/mensagens': () => import('@/pages/Mensagens'),
};

const prefetchRoute = (url: string) => {
  routePrefetchMap[url]?.();
};

const baseNavigation: NavEntry[] = [
  { label: 'Dashboard', icon: BarChart3, href: '/' },
  {
    label: 'Sua Loja',
    icon: Store,
    items: [
      { label: 'Produtos', icon: ShoppingBag, href: '/produtos' },
      { label: 'Vendas', icon: ShoppingCart, href: '/vendas' },
      { label: 'Clientes', icon: Users, href: '/clientes' },
    ],
  },
  {
    label: 'Motor de Vendas',
    icon: Zap,
    items: [
      { label: 'Live Shop', icon: Radio, href: '/lives' },
      { label: 'Automações', icon: Bot, href: '/automacoes' },
      { label: 'Mensagens', icon: MessageSquare, href: '/mensagens' },
    ],
  },
];

const buildNavigationWithPlugins = (
  activeSlugs: Set<string>,
): NavEntry[] => {
  return baseNavigation.map((entry) => {
    if (!isGroup(entry)) return entry;
    if (entry.label !== 'Sua Loja') return entry;

    const extraItems: NavItem[] = PLUGIN_DEFINITIONS
      .filter((def) => def.navLabel && def.navPath && activeSlugs.has(def.slug))
      .map((def) => ({
        label: def.navLabel!,
        icon: def.navIcon ?? def.icon,
        href: def.navPath!,
      }));

    if (extraItems.length === 0) return entry;

    return {
      ...entry,
      items: [...entry.items, ...extraItems],
    };
  });
};

function SidebarToggleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="1.5"
        y="1.5"
        width="15"
        height="15"
        rx="3.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <line
        x1="7"
        y1="1.5"
        x2="7"
        y2="16.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function getInitials(name?: string | null, email?: string): string {
  if (name) {
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return 'U';
}

interface AppSidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function AppSidebar({ collapsed, onToggleCollapse, mobileOpen = false, onMobileClose }: AppSidebarProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { profile, tenant } = useTenant();
  const { data: plugins } = usePluginsList();
  const activePluginSlugs = new Set(
    (plugins ?? [])
      .filter((p) => p.status === 'ativo' || p.status === 'trial')
      .map((p) => p.slug),
  );
  const navigation = buildNavigationWithPlugins(activePluginSlugs);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    'Sua Loja': true,
    'Motor de Vendas': true,
  });

  const displayName = profile?.full_name || 'Usuário';
  const email = user?.email || '';
  const initials = getInitials(profile?.full_name, email);
  const workspaceName = tenant?.name || 'Meu Workspace';
  const isSuperAdmin = user?.email && SUPER_ADMIN_EMAILS.includes(user.email);

  const touchStartX = useRef(0);
  const asideRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 80 && onMobileClose) {
      onMobileClose();
    }
  };

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const isActive = (href: string) =>
    pathname === href || (href !== '/' && pathname.startsWith(href));

  const isGroupActive = (group: NavGroup) =>
    group.items.some((item) => isActive(item.href));

  const sidebarContent = (
    <>
      {/* Header */}
      <div
        className={cn(
          'flex h-20 items-center shrink-0',
          collapsed ? 'justify-center px-3' : 'justify-between px-5'
        )}
      >
        <div className="relative overflow-hidden">
          <img
            src={collapsed ? '/logos/hermys-icon-lilac.svg' : '/logos/hermys-full-lilac.svg'}
            alt="Hermys"
            className={cn(
              'shrink-0 transition-all duration-300',
              collapsed ? 'h-5 w-auto opacity-80' : 'h-5 w-auto opacity-100'
            )}
          />
        </div>

        {!collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={mobileOpen ? onMobileClose : onToggleCollapse}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground/45 transition-colors hover:bg-[#EAEFFE]/60 hover:text-[#574BFB]"
              >
                <SidebarToggleIcon className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              {mobileOpen ? 'Fechar' : 'Recolher'}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Workspace */}
      {!collapsed ? (
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between rounded-xl border border-[#C2DBF6]/40 bg-[#EAEFFE]/30 px-3.5 py-2.5">
            <span className="text-sm font-semibold text-foreground truncate">
              {workspaceName}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-foreground/45" />
          </div>
        </div>
      ) : (
        <div className="py-3 flex justify-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggleCollapse}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground/45 transition-colors hover:bg-[#EAEFFE]/60 hover:text-[#574BFB]"
              >
                <SidebarToggleIcon className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="text-xs">
              Expandir
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 pt-2 pb-3 space-y-0.5">
        {navigation.map((entry) => {
          if (isGroup(entry)) {
            const GroupIcon = entry.icon;
            const open = openGroups[entry.label] ?? false;
            const groupActive = isGroupActive(entry);

            const groupButton = (
              <button
                onClick={() => {
                  if (collapsed) {
                    onToggleCollapse();
                    setOpenGroups((prev) => ({ ...prev, [entry.label]: true }));
                  } else {
                    toggleGroup(entry.label);
                  }
                }}
                className={cn(
                  'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-all duration-150',
                  collapsed && 'justify-center px-0',
                  groupActive && !open
                    ? 'text-[#988eff]'
                    : 'text-foreground/55 hover:bg-[#EAEFFE]/60 hover:text-[#574BFB]'
                )}
              >
                <GroupIcon
                  className={cn(
                    'h-[18px] w-[18px] shrink-0 transition-colors',
                    groupActive
                      ? 'text-[#988eff]'
                      : 'text-foreground/50 group-hover:text-[#574BFB]'
                  )}
                  strokeWidth={1.8}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1 text-left">{entry.label}</span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 text-foreground/40 transition-transform duration-200',
                        open && 'rotate-180'
                      )}
                    />
                  </>
                )}
              </button>
            );

            return (
              <div key={entry.label}>
                {collapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{groupButton}</TooltipTrigger>
                    <TooltipContent side="right" className="text-xs">{entry.label}</TooltipContent>
                  </Tooltip>
                ) : (
                  groupButton
                )}

                {!collapsed && (
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-200 ease-in-out',
                      open ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
                    )}
                  >
                    <div className="ml-7 space-y-0.5 py-1">
                      {entry.items.map((item) => {
                        const ItemIcon = item.icon;
                        const active = isActive(item.href);

                        if (item.comingSoon) {
                          return (
                            <div
                              key={item.label}
                              className="flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-medium cursor-default text-foreground/30"
                            >
                              <ItemIcon
                                className="h-[18px] w-[18px] shrink-0 text-foreground/30"
                                strokeWidth={1.8}
                              />
                              <span className="flex-1">{item.label}</span>
                              <span className="text-[10px] font-medium text-foreground/20 bg-foreground/[0.03] px-1.5 py-0.5 rounded-md">
                                breve
                              </span>
                            </div>
                          );
                        }

                        return (
                          <Link
                            key={item.label}
                            to={item.href}
                            onPointerEnter={() => prefetchRoute(item.href)}
                            className={cn(
                              'group/item flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] font-medium transition-all duration-150',
                              active
                                ? 'bg-[#988eff] text-white shadow-[0_0_12px_rgba(152,142,255,0.3)]'
                                : 'text-foreground/55 hover:bg-[#EAEFFE]/60 hover:text-[#574BFB] hover:translate-x-0.5'
                            )}
                          >
                            <ItemIcon
                              className={cn(
                                'h-[18px] w-[18px] shrink-0 transition-colors',
                                active
                                  ? 'text-white'
                                  : 'text-foreground/50 group-hover/item:text-[#988EFF]'
                              )}
                              strokeWidth={active ? 2 : 1.8}
                            />
                            <span>{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          const Icon = entry.icon;
          const active = isActive(entry.href);

          const linkElement = (
            <Link
              key={entry.label}
              to={entry.href}
              onPointerEnter={() => prefetchRoute(entry.href)}
              className={cn(
                'group/item flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-all duration-150',
                collapsed && 'justify-center px-0',
                active
                  ? 'bg-[#988eff] text-white shadow-[0_0_12px_rgba(152,142,255,0.3)]'
                  : 'text-foreground/55 hover:bg-[#EAEFFE]/60 hover:text-[#574BFB] hover:translate-x-0.5'
              )}
            >
              <Icon
                className={cn(
                  'h-[18px] w-[18px] shrink-0 transition-colors',
                  active
                    ? 'text-white'
                    : 'text-foreground/50 group-hover/item:text-[#988EFF]'
                )}
                strokeWidth={active ? 2 : 1.8}
              />
              {!collapsed && <span>{entry.label}</span>}
            </Link>
          );

          return collapsed ? (
            <Tooltip key={entry.label}>
              <TooltipTrigger asChild>{linkElement}</TooltipTrigger>
              <TooltipContent side="right" className="text-xs">{entry.label}</TooltipContent>
            </Tooltip>
          ) : (
            linkElement
          );
        })}
      </nav>

      {/* Footer — user profile */}
      <div className="shrink-0">
        <div className="mx-4 h-px bg-[#C2DBF6]/40" />

        <div className="px-3 py-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-150 hover:bg-[#EAEFFE]/60 cursor-pointer',
                  collapsed && 'justify-center px-0'
                )}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#988eff] to-[#7b6fcc] text-white">
                  <span className="text-xs font-semibold">{initials}</span>
                </div>
                {!collapsed && (
                  <>
                    <div className="flex flex-col flex-1 min-w-0 text-left">
                      <span className="text-[13px] font-medium text-foreground truncate">
                        {displayName}
                      </span>
                      <span className="text-[11px] text-foreground/50 truncate">
                        {email}
                      </span>
                    </div>
                    <MoreVertical className="h-4 w-4 shrink-0 text-foreground/40" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              side={collapsed ? 'right' : 'top'}
              align={collapsed ? 'end' : 'start'}
              className="w-52 mb-1"
            >
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{displayName}</p>
                <p className="text-xs text-muted-foreground">{email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate('/configuracoes')}
                onPointerEnter={() => prefetchRoute('/configuracoes')}
                className="cursor-pointer text-[13px]"
              >
                <Settings className="mr-2 h-3.5 w-3.5" />
                Configurações
              </DropdownMenuItem>
              {isSuperAdmin && (
                <DropdownMenuItem
                  onClick={() => navigate('/a12o')}
                  className="cursor-pointer text-[13px]"
                >
                  <Shield className="mr-2 h-3.5 w-3.5" />
                  Painel Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={signOut}
                className="cursor-pointer text-[13px]"
              >
                <LogOut className="mr-2 h-3.5 w-3.5" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 hidden md:flex h-screen flex-col transition-all duration-300 ease-in-out',
          collapsed ? 'w-[68px]' : 'w-[252px]'
        )}
        style={{ backgroundColor: 'hsl(236 100% 97%)' }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      <div
        className={cn(
          'fixed inset-0 z-50 md:hidden transition-opacity duration-300',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
      >
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onMobileClose}
        />
        <aside
          ref={asideRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className={cn(
            'absolute left-0 top-0 h-full w-[280px] flex flex-col shadow-2xl shadow-black/50 transition-transform duration-300 ease-out',
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          style={{ backgroundColor: 'hsl(236 100% 97%)' }}
        >
          {sidebarContent}
        </aside>
      </div>
    </TooltipProvider>
  );
}
